import * as braze from "@braze/web-sdk";

/**
 * Configuration for Braze SDK initialization
 */
interface BrazeConfig {
  apiKey: string;
  sdkEndpoint: string;
}

/**
 * Tracks whether Braze has been initialized in the current session
 */
let isInitialized = false;

/**
 * Tracks whether a user has been identified in the current session
 * This prevents duplicate changeUser calls
 */
let identifiedUserId: string | null = null;

/**
 * Gets Braze configuration from environment variables
 */
function getBrazeConfig(): BrazeConfig | null {
  const apiKey = import.meta.env.VITE_BRAZE_API_KEY;
  const sdkEndpoint = import.meta.env.VITE_BRAZE_SDK_ENDPOINT;

  if (!apiKey || !sdkEndpoint) {
    return null;
  }

  return {
    apiKey,
    sdkEndpoint,
  };
}

/**
 * Initializes the Braze Web SDK
 * Safe to call multiple times - will only initialize once per session
 * If Braze config is missing, initialization is skipped gracefully
 */
export function initializeBraze(): boolean {
  if (isInitialized) {
    return true;
  }

  const config = getBrazeConfig();
  if (!config) {
    return false;
  }

  try {
    braze.initialize(config.apiKey, {
      baseUrl: config.sdkEndpoint,
      enableLogging: import.meta.env.DEV, // Enable logging in development
    });

    braze.openSession();
    isInitialized = true;

    return true;
  } catch (error) {
    console.error("Failed to initialize Braze:", error);
    return false;
  }
}

/**
 * Identifies a user in Braze and sets minimal attributes
 * Should be called once per authenticated session after signup/login
 *
 * @param migiriUid - The user's Firebase UID (used as Braze external_id)
 * @param email - The user's email address (optional)
 */
export function identifyBrazeUser(
  migiriUid: string,
  email?: string
): void {
  // Skip if user already identified in this session
  if (identifiedUserId === migiriUid) {
    return;
  }

  // Ensure Braze is initialized
  if (!isInitialized) {
    const initialized = initializeBraze();
    if (!initialized) {
      return; // Gracefully skip if Braze not configured
    }
  }

  try {
    // Identify the user with their migiri_uid
    braze.changeUser(migiriUid);

    // Set user attributes
    const user = braze.getUser();
    if (user) {
      // Set email if available
      if (email) {
        user.setEmail(email);
      }

      // Set last login date to current timestamp
      user.setCustomUserAttribute("last_login_date", new Date().toISOString());
    }

    // Mark this user as identified for this session
    identifiedUserId = migiriUid;
  } catch (error) {
    console.error("Failed to identify Braze user:", error);
  }
}

/**
 * Resets the identified user state
 * Useful for testing or if user logs out and logs back in
 */
export function resetBrazeUserIdentification(): void {
  identifiedUserId = null;
}
